import { Button } from "@asosteam/asos-web-component-library-button";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { TextField } from "../../../components/text-field";
import { useVerificationDetails } from "../../../context/verification-details";
import {
  useLocaleProvider,
  useTranslate,
} from "../../../context/locale-provider";
import {
  verificationFormStatus,
  verificationCustomerStatus,
} from "../../../constants/verification-status";
import {
  setCountryCode,
  setDateOfBirth,
  setFirstName,
  setGraduationYear,
  setLastName,
  setOrganisation,
  setExternalFormVerificationUrl,
  setFormStatus,
} from "../../../state/verification-details/reducer";
import { CountrySelect } from "./country-select";
import { DateOfBirthField } from "./date-of-birth-field";
import { GraduationYearControl } from "./graduation-year-control";
import { SchoolFinderField } from "./school-finder-field";
import styles from "./styles";
import { createVerificationRequest } from "../../../utilities/create-verification-request";
import SheerIDLogo from "../../../assets/sheerid-logo.svg";
import { replaceJSX, isAuthenticated, promptLogin } from "../../utilities";
import sdkProvider from "../../../utilities/sdk-provider";
import { trackPageEvent } from "../../../utilities/trackPageEvent";

export default function VerificationForm({
  orgSearchUrl,
  sheerIdProgramId,
  successRedirectPath,
  countriesData,
  availableCountries,
  minDateOfBirth,
  minGraduationYear,
  assetsUrl,
  displayVerificationErrorPage,
  sheerIdLocale,
  setErrorPageType,
  customerStatus,
}) {
  const translate = useTranslate(useLocaleProvider());
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [state, dispatch] = useVerificationDetails();
  const {
    countryCode,
    organisation,
    firstName,
    lastName,
    dateOfBirth,
    graduationYear,
    formStatus,
  } = state.verificationDetails;

  const [validationErrors, setValidationErrors] = useState({
    countryCode: false,
    organisation: false,
    firstName: false,
    lastName: false,
    day: false,
    month: false,
    year: false,
    graduationYear: false,
    isAboveMinimumAge: false,
  });
  const [hasErrors, setHasErrors] = useState(false);

  const [isUserAuthenticated, setUserAuthenticated] = useState(false);

  useEffect(() => {
    const authStatus = async () => {
      const authResult = await isAuthenticated();
      setUserAuthenticated(authResult);
    };

    authStatus();
    const promptedToLogin = Boolean(localStorage.getItem("sv_login_prompted"));
    if (
      formStatus !== verificationFormStatus.PENDING &&
      customerStatus !== verificationCustomerStatus.PENDING_VERIFICATION
    ) {
      trackPageEvent({
        content: {
          layout: "student-verification-page-load",
          pageName: "verification form",
          loginPrompted: promptedToLogin,
        },
      });
    }
    if (promptedToLogin) localStorage.removeItem("sv_login_prompted");
  }, []);

  useEffect(() => {
    const populateFormWithCustomerData = async () => {
      const identity = await sdkProvider("identity");
      const lastName = await identity.customer.lastName();
      const firstName = await identity.customer.firstName();
      dispatch(setLastName(lastName));
      dispatch(setFirstName(firstName));
    };

    populateFormWithCustomerData();
  }, []);

  useEffect(() => {
    setHasErrors(
      Object.values({
        ...validationErrors,
        isAboveMinimumAge: false,
      }).find((item) => item)
    );
  }, [validationErrors]);

  const calculateMinAge = (selectedDateOfBirth) => {
    let isMinAge = true;

    if (dateOfBirth.year && dateOfBirth.month && dateOfBirth.day) {
      isMinAge = new Date(selectedDateOfBirth) <= new Date(minDateOfBirth);
    }

    return isMinAge;
  };

  const handleSubmit = async () => {
    const selectedDateOfBirth = `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`;

    const isMinimumAge = calculateMinAge(selectedDateOfBirth);

    const validationErrorState = {
      countryCode: !countryCode,
      organisation: !organisation?.name,
      firstName: !firstName,
      lastName: !lastName,
      day: !dateOfBirth?.day,
      month: !dateOfBirth?.month,
      year: !dateOfBirth?.year,
      graduationYear: !graduationYear,
      isAboveMinimumAge: !isMinimumAge,
    };

    setValidationErrors(validationErrorState);
    const errorsPresent = Object.values(validationErrorState).find(
      (item) => item
    );
    setHasErrors(errorsPresent);
    if (!errorsPresent) {
      const payload = {
        firstName,
        lastName,
        dateOfBirth: selectedDateOfBirth,
        organization: {
          id: organisation.id,
          name: organisation.name,
        },
        countryCode: countryCode.toUpperCase(),
        graduationYear: parseInt(graduationYear),
      };
      try {
        const response = await createVerificationRequest(
          payload,
          displayVerificationErrorPage,
          sheerIdLocale,
          successRedirectPath
        );
        dispatch(setFormStatus(response.verificationStatus));
        dispatch(
          setExternalFormVerificationUrl(response.externalVerificationUrl)
        );

        switch (response.verificationStatus) {
          case verificationFormStatus.SUCCESS:
            localStorage.setItem("sv_post_success", true);
            window.location.href = successRedirectPath;
            break;
          case verificationFormStatus.ERROR:
            setErrorPageType("sheerId");
        }
      } catch (e) {
        setErrorPageType("internal");
      }
    }
  };

  const onSubmit = async () => {
    setSubmitInProgress(true);
    if (isUserAuthenticated) {
      await handleSubmit();
    } else {
      await promptLogin();
    }
    setSubmitInProgress(false);
  };

  return (
    <div className={styles.container}>
      <h1 className="app_barnsley5">{translate("sv_form_header")}</h1>
      <div className={styles.formInfo}>
        <p>{translate("sv_form_p1")}</p>
        <p>{translate("sv_form_p2")}</p>
        <p>{translate("sv_form_p3")}</p>
      </div>
      <div className={styles.formControl}>
        <CountrySelect
          countriesData={countriesData}
          availableCountries={availableCountries}
          onItemClick={(e) => dispatch(setCountryCode(e.target.value))}
          hasError={validationErrors.countryCode}
          assetsUrl={assetsUrl}
        />
      </div>
      <div className={styles.formControl}>
        <SchoolFinderField
          orgSearchUrl={orgSearchUrl}
          organisation={organisation}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          sheerIdProgramId={sheerIdProgramId}
          sheerIdLocale={sheerIdLocale}
          onChange={(e) => {
            dispatch(
              setOrganisation({
                ...organisation,
                name: e.target.value,
              })
            );
            setValidationErrors({
              ...validationErrors,
              organisation: !e.target.value,
            });
          }}
        />
      </div>
      <div className={styles.formControl}>
        <label htmlFor="firstName" className="app_london3">
          {translate("sv_form_first_name_label")}:
        </label>
        <TextField
          id="firstName"
          ariaLabel={translate("sv_form_first_name_label")}
          initialValue={firstName}
          onChange={(e) => {
            dispatch(setFirstName(e.target.value));
            setValidationErrors({
              ...validationErrors,
              firstName: !e.target.value,
            });
          }}
          errorMessage={
            validationErrors.firstName
              ? translate("sv_form_first_name_error")
              : ""
          }
          required
          description={translate("sv_form_tip")}
        />
      </div>
      <div className={styles.formControl}>
        <label htmlFor="lastName" className="app_london3">
          {translate("sv_form_last_name_label")}:
        </label>
        <TextField
          id="lastName"
          ariaLabel={translate("sv_form_last_name_label")}
          initialValue={lastName}
          onChange={(e) => {
            dispatch(setLastName(e.target.value));
            setValidationErrors({
              ...validationErrors,
              lastName: !e.target.value,
            });
          }}
          errorMessage={
            validationErrors.lastName
              ? translate("sv_form_last_name_error")
              : ""
          }
          required
          description={translate("sv_form_tip")}
        />
      </div>
      <div className={styles.formControl}>
        <DateOfBirthField
          dateOfBirth={dateOfBirth}
          minDateOfBirth={minDateOfBirth}
          onChange={(e) => {
            dispatch(setDateOfBirth(e));
          }}
          validationErrors={validationErrors}
        />
      </div>
      <div className={styles.formControl}>
        <GraduationYearControl
          selectedValue={graduationYear}
          minGraduationYear={minGraduationYear}
          onSelect={(e) => {
            dispatch(setGraduationYear(e));
            setValidationErrors({
              ...validationErrors,
              graduationYear: !e,
            });
          }}
          errorMessage={
            validationErrors.graduationYear
              ? translate("sv_form_year_error")
              : ""
          }
        />
      </div>
      <span className={styles.tsAndCs}>
        <a href={translate("tandcsLink")}>{translate("sv_form_tcs_link")}</a>
      </span>

      <Button
        className="app_button"
        onClick={onSubmit}
        disabled={submitInProgress || hasErrors}
        isInProgress={submitInProgress}
      >
        {translate("sv_form_verify_me_cta")}
      </Button>
      <div className={styles.footer}>
        <SheerIDLogo aria-label="SheerID" role="img" />
        <p>
          {replaceJSX(
            translate("sv_form_footer"),
            "%{privacypolicylink}",
            <a key="privacy-policy-link" href={translate("privacypolicyLink")}>
              {translate("sv_form_footer_linktext")}
            </a>
          )}
        </p>
        <a
          className={styles.help}
          href={`https://offers.sheerid.com/sheerid/help-center/?pid=${sheerIdProgramId}&locale=${sheerIdLocale}`}
        >
          {translate("sv_form_help")}
        </a>
      </div>
    </div>
  );
}

VerificationForm.propTypes = {
  orgSearchUrl: PropTypes.string.isRequired,
  sheerIdProgramId: PropTypes.string.isRequired,
  successRedirectPath: PropTypes.string.isRequired,
  countriesData: PropTypes.array.isRequired,
  availableCountries: PropTypes.array.isRequired,
  minDateOfBirth: PropTypes.string.isRequired,
  minGraduationYear: PropTypes.string.isRequired,
  assetsUrl: PropTypes.string.isRequired,
  displayVerificationErrorPage: PropTypes.func.isRequired,
  sheerIdLocale: PropTypes.string.isRequired,
  setErrorPageType: PropTypes.func.isRequired,
  customerStatus: PropTypes.string.isRequired,
};
